<template>
  <!-- 商品促销规则配置 -->
  <div class="rule-config-container">
    <el-form :model="form" label-width="120px" ref="form">
      <el-form-item label="启用促销规则">
        <el-switch v-model="form.enable_promotion" active-text="开启" inactive-text="关闭"></el-switch>
      </el-form-item>

      <div v-if="form.enable_promotion" class="rules-section">
        <el-form-item label="促销规则列表">
          <el-button type="primary" size="small" @click="addRule" icon="el-icon-plus">添加规则</el-button>
        </el-form-item>

        <el-table :data="form.rules" style="width: 100%" border>
          <el-table-column label="购买数量" width="200">
            <template slot-scope="scope">
              <el-input-number 
                v-model="scope.row.quantity" 
                :min="1" 
                :max="999"
                placeholder="数量"
                size="small"
              ></el-input-number>
            </template>
          </el-table-column>
          
          <el-table-column label="折扣率" width="250">
            <template slot-scope="scope">
              <el-slider 
                v-model="scope.row.discount" 
                :min="0" 
                :max="1" 
                :step="0.01"
                :format-tooltip="formatDiscount"
                style="width: 180px; display: inline-block; vertical-align: middle;"
              ></el-slider>
              <span style="margin-left: 10px;">{{ formatDiscount(scope.row.discount) }}</span>
            </template>
          </el-table-column>
          
          <el-table-column label="操作" width="100">
            <template slot-scope="scope">
              <el-button 
                type="danger" 
                size="small" 
                icon="el-icon-delete"
                @click="removeRule(scope.$index)"
              >删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="rule-example">
          <h4>规则说明：</h4>
          <ul>
            <li>购买数量：达到该数量时享受对应折扣</li>
            <li>折扣率：0.98表示9.8折，0.95表示9.5折，以此类推</li>
            <li>系统会自动按数量从小到大应用最优折扣</li>
          </ul>
          <h4>示例：</h4>
          <p>购买2个9.8折，3个9.5折，4个及以上9折</p>
        </div>

        <el-form-item label="预览JSON" v-if="showJsonPreview">
          <el-input 
            type="textarea" 
            :value="jsonPreview" 
            :rows="6"
            readonly
          ></el-input>
        </el-form-item>
        
        <el-form-item>
          <el-checkbox v-model="showJsonPreview">显示JSON预览</el-checkbox>
        </el-form-item>
      </div>

      <el-form-item v-if="id">
        <el-button type="primary" @click="saveRules" :loading="loading">保存规则</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { ObjectChangedValue } from "@/js/utils.js";

export default {
  name: "RuleConfig",
  props: {
    info: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      form: {
        enable_promotion: false,
        rules: []
      },
      temp: {},
      loading: false,
      showJsonPreview: false,
      id: ''
    };
  },
  computed: {
    jsonPreview() {
      const config = {
        enable_promotion: this.form.enable_promotion,
        rules: this.form.rules.map(rule => ({
          quantity: rule.quantity,
          discount: rule.discount
        }))
      };
      return JSON.stringify(config, null, 2);
    }
  },
  watch: {
    info: {
      handler: function(nv) {
        if (nv && nv.id) {
          this.id = nv.id;
          this.initForm(nv);
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.id = this.$route.query.id || '';
  },
  methods: {
    initForm(info) {
      // 解析现有的rule_config
      if (info.rule_config) {
        try {
          const config = typeof info.rule_config === 'string' 
            ? JSON.parse(info.rule_config) 
            : info.rule_config;
          
          this.form = {
            enable_promotion: config.enable_promotion || false,
            rules: (config.rules || []).map(rule => ({
              quantity: rule.quantity || 1,
              discount: rule.discount || 1
            })).sort((a, b) => a.quantity - b.quantity)
          };
        } catch (e) {
          console.error('解析促销规则失败:', e);
          this.form = {
            enable_promotion: false,
            rules: []
          };
        }
      } else {
        this.form = {
          enable_promotion: false,
          rules: []
        };
      }
      this.temp = JSON.parse(JSON.stringify(this.form));
    },

    addRule() {
      // 获取当前最大数量
      const maxQuantity = this.form.rules.length > 0 
        ? Math.max(...this.form.rules.map(r => r.quantity))
        : 0;
      
      this.form.rules.push({
        quantity: maxQuantity + 1,
        discount: 0.95
      });
      
      // 自动排序
      this.sortRules();
    },

    removeRule(index) {
      this.form.rules.splice(index, 1);
    },

    sortRules() {
      this.form.rules.sort((a, b) => a.quantity - b.quantity);
    },

    formatDiscount(value) {
      return (value * 10).toFixed(1) + '折';
    },

    validateRules() {
      if (!this.form.enable_promotion) {
        return true;
      }

      if (this.form.rules.length === 0) {
        this.$message.warning('请至少添加一条促销规则');
        return false;
      }

      // 验证每条规则
      for (let i = 0; i < this.form.rules.length; i++) {
        const rule = this.form.rules[i];
        
        if (!rule.quantity || rule.quantity < 1) {
          this.$message.warning(`第${i + 1}条规则的购买数量必须大于0`);
          return false;
        }

        if (!rule.discount || rule.discount <= 0 || rule.discount > 1) {
          this.$message.warning(`第${i + 1}条规则的折扣率必须在0-1之间`);
          return false;
        }
      }

      // 检查是否有重复的数量
      const quantities = this.form.rules.map(r => r.quantity);
      const uniqueQuantities = new Set(quantities);
      if (quantities.length !== uniqueQuantities.size) {
        this.$message.warning('存在重复的购买数量，请检查');
        return false;
      }

      return true;
    },

    saveRules() {
      if (!this.validateRules()) {
        return;
      }

      this.loading = true;

      // 构建rule_config JSON
      const ruleConfig = {
        enable_promotion: this.form.enable_promotion,
        rules: this.form.rules.map(rule => ({
          quantity: rule.quantity,
          discount: parseFloat(rule.discount.toFixed(2))
        }))
      };

      const data = {
        id: this.id,
        rule_config: JSON.stringify(ruleConfig)
      };

      this.$axios({
        url: "/user/product/update",
        method: "post",
        data: data,
      }).then((res) => {
        this.$notify({
          message: "促销规则保存成功",
          duration: 2000,
          type: "success",
        });
        this.temp = JSON.parse(JSON.stringify(this.form));
        this.loading = false;
        this.$emit('saved', ruleConfig);
      }).catch(() => {
        this.loading = false;
      });
    },

    // 供父组件调用的方法，返回规则数据
    getRuleData() {
      if (!this.form.enable_promotion || this.form.rules.length === 0) {
        return null;
      }

      const ruleConfig = {
        enable_promotion: this.form.enable_promotion,
        rules: this.form.rules.map(rule => ({
          quantity: rule.quantity,
          discount: parseFloat(rule.discount.toFixed(2))
        }))
      };

      return JSON.stringify(ruleConfig);
    }
  }
};
</script>

<style lang="less" scoped>
.rule-config-container {
  padding: 20px;

  .rules-section {
    margin-top: 20px;
    padding: 20px;
    background-color: #f5f7fa;
    border-radius: 4px;
  }

  .rule-example {
    margin-top: 20px;
    padding: 15px;
    background-color: #fff;
    border-left: 3px solid #409EFF;
    
    h4 {
      margin: 0 0 10px 0;
      color: #303133;
      font-size: 14px;
    }

    ul {
      margin: 10px 0;
      padding-left: 20px;
      
      li {
        margin: 5px 0;
        color: #606266;
        font-size: 13px;
      }
    }

    p {
      margin: 10px 0 0 0;
      color: #409EFF;
      font-weight: bold;
      font-size: 14px;
    }
  }

  ::v-deep .el-table {
    margin-top: 10px;
  }
}
</style>
